import React, { useState, useEffect } from 'react';
import { Card, Select, Button, message, Typography, Space, Alert, Spin, Row, Col } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { cookieOptions } from '../utils/cookieConfig';
import { parseRoles } from '../utils/parseRoles';

const { Title, Text } = Typography;
const { Option } = Select;

const apiUrl = (import.meta.env.VITE_API_URL || import.meta.env.VITE_NODE_API_URL || 'http://localhost:3000').replace(/\/+$/, '');

const isProductionEnv = import.meta.env.VITE_ENV === 'production' || import.meta.env.VITE_ENVIRONMENT === 'production';

const getSafeRedirect = () => {
    const rawRedirect = new URLSearchParams(window.location.search).get('redirect') || '/';
    try {
        const url = new URL(rawRedirect, window.location.origin);
        if (url.origin !== window.location.origin) return '/';
        return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
        return '/';
    }
};

const DevQuickLogin = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isProductionEnv) {
            setError('Dev quick login is not available in production');
            setFetching(false);
            return;
        }
        let cancelled = false;
        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${apiUrl}/auth/dev-quick-users`);
                if (cancelled) return;
                const data = response.data.data || response.data;
                setUsers(Array.isArray(data) ? data : []);
            } catch (err) {
                if (cancelled) return;
                const msg = err.response?.data?.data?.error || err.response?.data?.message || 'Failed to fetch users';
                setError(msg);
            } finally {
                if (!cancelled) setFetching(false);
            }
        };
        fetchUsers();
        return () => { cancelled = true; };
    }, []);

    const handleLogin = async () => {
        if (!selectedUser) {
            message.warning('Please select a user');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${apiUrl}/auth/dev-quick-login`, { userId: selectedUser });
            const responseData = response.data?.data || response.data;
            if (!responseData?.token || !responseData?.user) {
                message.error('Login failed. Please try again.');
                return;
            }

            const cookies = new Cookies();
            cookies.set('tokenR', responseData.token, cookieOptions);
            cookies.set('userR', {
                id: responseData.user.id,
                name: responseData.user.name,
                email: responseData.user.email,
                roles: parseRoles(responseData.user.roles),
                office: responseData.user.office_id,
                department: responseData.user.department?.name || null,
                department_id: responseData.user.department_id,
                title: responseData.user.title || null,
                isManager: responseData.user.isManager || false,
                status: 'active',
            }, cookieOptions);
            cookies.set('userRId', responseData.user.id, cookieOptions);

            window.location.href = getSafeRedirect();
        } catch (err) {
            const msg = err.response?.data?.data?.error || err.response?.data?.message || 'Login failed. Please try again.';
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
                <Alert message="Dev Login Unavailable" description={error} type="error" showIcon />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5' }}>
            <Row justify="center" style={{ width: '100%' }}>
                <Col xs={24} sm={20} md={16} lg={12} xl={10}>
                    <Card style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} bordered={false}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <LoginOutlined style={{ fontSize: 48, color: '#FED230', marginBottom: 16 }} />
                                <Title level={2}>Dev Quick Login</Title>
                                <Text type="secondary">Select a user to log in without Google OAuth</Text>
                            </div>

                            {fetching ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Spin size="large" />
                                    <div style={{ marginTop: 10 }}>Loading users...</div>
                                </div>
                            ) : (
                                <>
                                    <Select
                                        size="large"
                                        placeholder="Select a user"
                                        style={{ width: '100%' }}
                                        onChange={setSelectedUser}
                                        showSearch
                                        filterOption={(input, option) => {
                                            const searchText = option['data-search'] || '';
                                            return searchText.toLowerCase().includes(input.toLowerCase());
                                        }}
                                    >
                                        {users.map(user => {
                                            const name = (user.first_name || user.last_name)
                                                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                                : user.email;
                                            const dept = user.department?.name || '';
                                            const office = user.office?.full_name || '';
                                            const location = [dept, office].filter(Boolean).join(' - ');

                                            return (
                                                <Option key={user.id} value={user.id} data-search={`${name} ${user.email} ${location}`}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                                                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                                                            {name}
                                                            {location && <span style={{ fontSize: '13px', fontWeight: 400, color: '#8c8c8c', marginLeft: 8 }}>({location})</span>}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{user.email}</div>
                                                    </div>
                                                </Option>
                                            );
                                        })}
                                    </Select>

                                    <Button
                                        type="primary"
                                        size="large"
                                        block
                                        icon={<LoginOutlined />}
                                        onClick={handleLogin}
                                        loading={loading}
                                        disabled={!selectedUser}
                                        style={{ backgroundColor: '#FED230', borderColor: '#FED230', color: '#000' }}
                                    >
                                        Log In
                                    </Button>
                                </>
                            )}
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DevQuickLogin;
